import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { api } from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useDashboardMode } from '@/store/mode';
import { TrustMeter } from '@/components/product/TrustBadge';
import { MemberBadge } from '@/components/MemberBadge';

interface OverviewData {
  walletBalanceGBP: number;
  pendingEscrowGBP: number;
  trustScore?: number;
  activeOrders: number;
  activeSales: number;
  notifications: { id: string; message: string; date: string; href?: string }[];
}
interface MyListing { id: string; title: string; priceGBP: number; views: number; saves: number; state: string; imageUrl?: string }

const QUICK_SELLER = [
  { label: 'List an item', href: '/dashboard/sell', icon: '+', primary: true },
  { label: 'My listings', href: '/dashboard/listings', icon: '🏷️' },
  { label: 'Sales', href: '/dashboard/sales', icon: '📦' },
  { label: 'Offers', href: '/dashboard/offers', icon: '💬' },
];
const QUICK_ALL = [
  { label: 'Browse', href: '/c/all', icon: '🛍️' },
  { label: 'Orders', href: '/dashboard/orders', icon: '📦' },
  { label: 'Wallet', href: '/dashboard/wallet', icon: '💸' },
  { label: 'Profile', href: '/dashboard/profile', icon: '👤' },
];

/**
 * Seller/buyer dashboard. Everything is driven by the signed-in account's real
 * data, so a freshly registered user starts blank and the page fills in as they
 * list, buy and sell. No demo numbers.
 */
export default function Overview() {
  const user = useAuthStore((s) => s.user);
  // Follows the same "Buying / Selling" toggle as the dashboard nav
  // (DashboardLayout.tsx) - same persisted-per-account logic, same shared
  // store, so the sidebar and this page never disagree about which view is active.
  const storedMode = useDashboardMode((s) => s.mode);
  const storedUserId = useDashboardMode((s) => s.userId);
  const isSeller = ((storedMode && storedUserId === user?.id) ? storedMode : 'SELLER') === 'SELLER';

  const { data: ov } = useQuery<OverviewData>({
    queryKey: ['overview'],
    queryFn: async () => (await api.get('/me/overview')).data,
    retry: false,
  });
  const { data: listings = [] } = useQuery<MyListing[]>({
    queryKey: ['my-listings'],
    queryFn: async () => (await api.get('/me/listings')).data,
    retry: false,
    enabled: isSeller,
  });

  // Inline price editing - a cut price alerts everyone watching the item.
  const [editing, setEditing] = useState<string | null>(null);
  const [draftPrice, setDraftPrice] = useState('');
  const qc = useQueryClient();
  const savePrice = useMutation({
    mutationFn: async (p: { id: string; priceGBP: number }) =>
      (await api.patch(`/listings/${p.id}/price`, { priceGBP: p.priceGBP })).data,
    onSuccess: (res: any) => {
      setEditing(null);
      if (res?.dropped) {
        toast.success(
          res.watchersNotified
            ? `Price dropped - ${res.watchersNotified} watcher${res.watchersNotified === 1 ? '' : 's'} notified.`
            : 'Price dropped and updated.',
        );
      } else {
        toast.success('Price updated.');
      }
      qc.invalidateQueries({ queryKey: ['my-listings'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update the price.'),
  });

  const balance = ov?.walletBalanceGBP ?? 0;
  const pending = ov?.pendingEscrowGBP ?? 0;
  const activeOrders = ov?.activeOrders ?? 0;
  const activeSales = ov?.activeSales ?? 0;
  const notifications = ov?.notifications ?? [];
  const listedValue = listings.reduce((a, l) => a + l.priceGBP, 0);
  const brandNew = balance === 0 && activeOrders === 0 && activeSales === 0 && listings.length === 0;

  const quick = [
    ...(isSeller ? QUICK_SELLER : []),
    ...QUICK_ALL,
  ];

  return (
    <div className="space-y-6">
      {/* ── Hero ── shares the Souqli Member ground so every dashboard page
           reads as one surface rather than a set of unrelated panels. */}
      <section className="section-dark relative overflow-hidden rounded-bold text-cream shadow-frame p-5 sm:p-6 md:p-8"
               style={{ background: '#221A12' }}>
        <div className="absolute inset-0 mashrabiya opacity-[0.07] pointer-events-none" />
        <span className="absolute -right-16 -top-20 w-72 h-72 rounded-full bg-gold/20 blur-3xl pointer-events-none" />
        <span className="absolute -left-10 -bottom-24 w-64 h-64 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="pill bg-gold text-ink text-[10px]">{isSeller ? '🏷️ Seller account' : '🛍️ Buyer account'}</span>
            {/*
              Two different things used to both read "Verified" here: this one
              is identity (ID checked), the badge beside it is membership (paid).
              Naming them apart is the fix - a user who can't tell two badges
              apart trusts neither.
            */}
            {user?.verified
              ? <span className="pill bg-sage/30 text-sage text-[10px]">✅ ID verified</span>
              : <Link
                  to="/dashboard/profile#identity"
                  title="Verify your ID"
                  className="pill bg-cream/10 text-cream/70 hover:bg-cream/20 hover:text-cream text-[10px] inline-flex items-center gap-1 transition cursor-pointer"
                >
                  ID not verified <span aria-hidden>&rarr;</span>
                </Link>}
            <MemberBadge />
          </div>
          <h1 className="display text-3xl md:text-4xl leading-tight">
            Welcome{user?.name ? ', ' : ' '}<span className="text-gold">{user?.name?.split(' ')[0] ?? 'to Souqli'}</span>
          </h1>
          <p className="text-cream/75 text-sm mt-1.5">
            {brandNew
              ? (isSeller ? 'Your store is ready - list your first item to get started.' : 'Your account is ready - start browsing to make your first order.')
              : 'Here’s where things stand today.'}
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <Stat label={isSeller ? 'Wallet balance' : 'Wallet'} value={`£${balance.toFixed(2)}`} />
            <Stat label="On hold" value={`£${pending.toFixed(2)}`} />
            <Stat label="Active orders" value={String(activeOrders)} />
            <Stat label={isSeller ? 'Active sales' : 'Membership'} value={isSeller ? String(activeSales) : 'Free'} />
          </div>
        </div>
      </section>

      {/* ── Quick actions ── */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {quick.map((a) => (
          <Link
            key={a.label}
            to={a.href}
            className={clsx(
              'flex flex-col items-center justify-center gap-2 py-4 rounded-luxe text-center text-xs font-medium transition',
              (a as { primary?: boolean }).primary
                ? 'bg-gold text-ink shadow-glow hover:brightness-105'
                : 'bg-white border border-warmline text-ink hover:border-gold hover:text-gold-dark',
            )}
          >
            <span className="text-lg leading-none">{a.icon}</span>
            <span className="leading-tight">{a.label}</span>
          </Link>
        ))}
      </section>

      {/* ── Listings / orders + activity ── */}
      <section className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="bg-white rounded-luxe border border-warmline overflow-hidden">
          <div className="px-5 py-4 border-b border-warmline flex items-center justify-between">
            <h2 className="font-semibold text-sm text-ink">{isSeller ? 'Your listings' : 'Your orders'}</h2>
            <Link to={isSeller ? '/dashboard/listings' : '/dashboard/orders'} className="text-[11px] text-mute hover:text-gold-dark">View all &rarr;</Link>
          </div>
          {isSeller ? (
            listings.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs min-w-[440px]">
                  <thead>
                    <tr className="border-b border-warmline">
                      <th className="text-left  px-5 py-2.5 stamp text-mute text-[9px]">ITEM</th>
                      <th className="text-right px-5 py-2.5 stamp text-mute text-[9px]">PRICE</th>
                      <th className="text-right px-5 py-2.5 stamp text-mute text-[9px]">VIEWS</th>
                      <th className="text-right px-5 py-2.5 stamp text-mute text-[9px]">SAVES</th>
                      <th className="text-right px-5 py-2.5 stamp text-mute text-[9px]">STATUS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.slice(0, 6).map((l) => (
                      <tr key={l.id} className="border-b border-warmline/50 hover:bg-cream/40 transition">
                        <td className="px-5 py-3 text-ink font-medium truncate max-w-[180px]">{l.title}</td>
                        <td className="px-5 py-3 text-right">
                          {editing === l.id ? (
                            <span className="inline-flex items-center gap-1 justify-end">
                              <span className="text-mute">£</span>
                              <input
                                autoFocus
                                type="number"
                                min={1}
                                value={draftPrice}
                                onChange={(e) => setDraftPrice(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') savePrice.mutate({ id: l.id, priceGBP: Number(draftPrice) });
                                  if (e.key === 'Escape') setEditing(null);
                                }}
                                className="w-20 text-right border border-gold rounded px-1.5 py-0.5 text-[13px] outline-none"
                              />
                              <button
                                onClick={() => savePrice.mutate({ id: l.id, priceGBP: Number(draftPrice) })}
                                disabled={savePrice.isPending || !Number(draftPrice)}
                                className="text-[11px] text-gold-dark font-semibold hover:underline"
                              >Save</button>
                              <button onClick={() => setEditing(null)} className="text-[11px] text-mute hover:underline">✕</button>
                            </span>
                          ) : (
                            <button
                              onClick={() => { setEditing(l.id); setDraftPrice(String(Math.round(l.priceGBP))); }}
                              title="Change price"
                              className="text-ink hover:text-gold-dark group/price inline-flex items-center gap-1"
                            >
                              £{l.priceGBP.toFixed(0)}
                              <span className="text-[10px] text-mute opacity-0 group-hover/price:opacity-100 transition">✎</span>
                            </button>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right text-mute">{l.views}</td>
                        <td className="px-5 py-3 text-right text-mute">{l.saves}</td>
                        <td className="px-5 py-3 text-right"><span className="pill text-[9px] bg-cream-deep text-mute">{l.state.toLowerCase().replace('_', ' ')}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Empty icon="🏷️" title="No listings yet" body="List your first item and it’ll show up here with live views and saves." cta={{ label: 'List an item', href: '/dashboard/sell' }} />
            )
          ) : (
            <Empty
              icon="📦"
              title={activeOrders ? `${activeOrders} active order${activeOrders > 1 ? 's' : ''}` : 'No orders yet'}
              body={activeOrders ? 'Track delivery and confirm collection from your Orders page.' : 'Browse the marketplace and your orders will appear here.'}
              cta={activeOrders ? { label: 'View orders', href: '/dashboard/orders' } : { label: 'Browse items', href: '/c/all' }}
            />
          )}
        </div>

        <div className="bg-white rounded-luxe border border-warmline overflow-hidden">
          <div className="px-5 py-4 border-b border-warmline flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-sage" />
            <h2 className="font-semibold text-sm text-ink">Activity</h2>
          </div>
          {notifications.length ? (
            <ul className="divide-y divide-warmline/50">
              {notifications.map((n) => (
                <li key={n.id} className="px-5 py-3.5">
                  <div className="text-xs text-ink leading-snug">{n.message}</div>
                  <div className="text-[10px] text-mute mt-1">{new Date(n.date).toLocaleDateString()}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-5 py-12 text-center text-mute text-xs">Nothing here yet - activity from offers, sales and payouts will show up here.</div>
          )}
        </div>
      </section>

      {/* ── Seller status (sellers only) ── */}
      {isSeller && (
        <section className="bg-white rounded-luxe border border-warmline overflow-hidden">
          <div className="px-6 py-5 border-b border-warmline">
            <h2 className="font-semibold text-sm text-ink">Seller status</h2>
            <p className="text-[11px] text-mute mt-0.5">
              {listings.length} listing{listings.length === 1 ? '' : 's'} · £{listedValue.toFixed(0)} listed · {activeSales} active sale{activeSales === 1 ? '' : 's'}
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-6 py-5">
            {[
              { icon: '✅', label: 'Verified Seller', req: '5 sales + ID check', on: !!user?.verified },
              { icon: '🛡', label: 'Trusted', req: '25 sales, 4.8★', on: false },
              { icon: '🏆', label: 'Gold', req: '50 sales, 4.9★', on: false },
              { icon: '✨', label: 'Curated', req: 'Invite only', on: false },
            ].map((t) => (
              <div key={t.label} className={clsx('rounded-luxe border p-4 text-center', t.on ? 'border-gold/30 bg-gold/5' : 'border-warmline bg-cream/50 opacity-70')}>
                <div className="text-2xl mb-2">{t.icon}</div>
                <div className="text-[11px] font-medium">{t.label}</div>
                <div className="text-[10px] text-mute mt-1 leading-snug">{t.req}</div>
                {t.on && <div className="stamp text-[8px] text-sage mt-2">UNLOCKED</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Wallet + getting started ── */}
      <section className="grid md:grid-cols-2 gap-6 items-start">
        <div className="section-dark text-cream rounded-luxe p-6" style={{ background: '#221A12' }}>
          <div className="stamp text-cream/50 text-[9px]">AVAILABLE BALANCE</div>
          <div className="display text-4xl text-gold mt-1">£{balance.toFixed(2)}</div>
          <div className="text-[11px] text-cream/50 mt-2">£{pending.toFixed(2)} on hold{isSeller ? ' until buyers confirm collection' : ''}.</div>
          <Link to="/dashboard/wallet" className="btn btn-gold w-full text-center text-sm mt-5">{isSeller ? 'Manage payouts' : 'View wallet'}</Link>
        </div>
        <div className="bg-white rounded-luxe border border-warmline p-6">
          {typeof ov?.trustScore === 'number' && (
            <TrustMeter score={ov.trustScore} className="mb-5 pb-5 border-b border-warmline" />
          )}
          <div className="stamp text-mute text-[9px]">GET STARTED</div>
          <ul className="mt-3 space-y-2.5 text-sm">
            <Checklist done label="Create your account" />
            <Checklist done={!!user?.verified} label="Verify your ID" href="/dashboard/profile#identity" />
            <Checklist done={isSeller ? listings.length > 0 : activeOrders > 0} label={isSeller ? 'List your first item' : 'Place your first order'} />
            {isSeller && <Checklist done={balance > 0 || activeSales > 0} label="Make your first sale" />}
          </ul>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-cream/5 rounded-luxe px-4 py-3 border border-white/10">
      <div className="display text-2xl text-gold leading-none">{value}</div>
      <div className="stamp text-cream/50 text-[9px] mt-1.5">{label}</div>
    </div>
  );
}

function Empty({ icon, title, body, cta }: { icon: string; title: string; body: string; cta: { label: string; href: string } }) {
  return (
    <div className="px-6 py-12 text-center">
      <div className="text-3xl mb-2">{icon}</div>
      <div className="font-semibold text-sm text-ink">{title}</div>
      <p className="text-xs text-mute mt-1 max-w-[36ch] mx-auto leading-relaxed">{body}</p>
      <Link to={cta.href} className="btn btn-outline btn-sm mt-4">{cta.label}</Link>
    </div>
  );
}

function Checklist({ done, label, href }: { done?: boolean; label: string; href?: string }) {
  return (
    <li className="flex items-center gap-2.5">
      <span className={clsx('w-4 h-4 rounded-full grid place-items-center text-[9px] shrink-0', done ? 'bg-sage text-cream' : 'border border-warmline text-transparent')}>✓</span>
      {!done && href
        ? <Link to={href} className="text-ink hover:text-gold-dark underline-offset-2 hover:underline inline-flex items-center gap-1">{label} <span aria-hidden>&rarr;</span></Link>
        : <span className={done ? 'text-mute' : 'text-ink'}>{label}</span>}
    </li>
  );
}
